/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { useEffect, useState, useCallback } from 'react';
import { TaskExecutionController } from '@karya/generic-task-react';

export type UseNextStrategy = 'MANUAL' | 'AUTO' | 'DELAY';

export interface UseNextOptions {
  controller: TaskExecutionController | null;
  strategy: UseNextStrategy;
  teardownFn?: (next: () => void) => void;
  delay?: number;
  isCurrent: boolean;
}

/**
 * Hook to handle component advancement and teardown registration.
 */
export function useNext({ controller, strategy, teardownFn, delay, isCurrent }: UseNextOptions) {
  const [showManualButton, setShowManualButton] = useState(strategy === 'MANUAL');

  const controllerNext = controller?.next;

  // Register the teardown function with the controller
  useEffect(() => {
    if (!controller || !isCurrent || !teardownFn) {
      return;
    }

    controller?.setSubmitHandler(teardownFn);
  }, [controller, isCurrent, teardownFn]);

  // Wrapper for next that handles teardown if not registered or if manual call needed
  const handleNext = useCallback(() => {
    // If controller is null (for completed assignments), do nothing
    if (!controller || !controllerNext) {
      return;
    }
    // If teardownFn is provided and we are calling manually,
    // controller.next() will trigger it via the registered handler.
    // If NO teardownFn was registered (e.g. Platform component),
    // controller.next() just advances.
    if (isCurrent) {
      controllerNext();
    }
  }, [controller, controllerNext, isCurrent]);

  // Handle AUTO and DELAY strategies
  useEffect(() => {
    // If controller is null (for completed assignments), do nothing
    if (!controller || !controllerNext) {
      return;
    }

    // Only advance if we are the *current* component.
    if (!isCurrent) {
      return;
    }

    // If strategy is MANUAL, we just show button.
    if (strategy === 'MANUAL') {
      setShowManualButton(true);
      return;
    }

    // If not MANUAL, we hide button initially (or always).
    setShowManualButton(false);

    if (strategy === 'AUTO') {
      controllerNext();
    } else if (strategy === 'DELAY') {
      const timeoutId = setTimeout(() => {
        controllerNext();
      }, delay ?? 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [controller, strategy, delay, controllerNext, isCurrent]);

  return {
    next: handleNext,
    showManualButton,
  };
}
